'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'

import { fetchFavoriteChange, useBecomeFan } from '@/features/clubs/hooks'
import {
  blockFromAvailability,
  blockFromError,
  FAVORITE_CHANGE_KEY,
  type FavoriteChangeBlock
} from '@/features/clubs/lib/favoriteChange'
import { useUserMe } from '@/hooks/useUserMe'
import { showToast } from '@/providers/ToastProvider/ToastMessage'
import Button from '@/shared/ui/Button'
import { HeartIcon } from '@/ui/icons/HeartIcon'

import S from './BecomeFanButton.module.scss'
import { FavoriteBlockedDialog, FavoriteSwitchDialog } from './FavoriteDialogs'

interface BecomeFanButtonProps {
  clubId: string
  /** The club / athlete name shown in the switch dialog. */
  name: string
  /** BAT-1106/1107: an athlete profile. */
  isAthlete?: boolean
}

type Step =
  | { kind: 'idle' }
  | { kind: 'switch'; currentName: string }
  | { kind: 'blocked'; block: FavoriteChangeBlock }

/**
 * «Become a fan» (BAT-1107, US-ATH-14 / 15.1 / 15.2), for a club and an
 * athlete alike: without a favourite the pick is made at once; with one
 * (a club, an athlete or a Requested Club) the switch is checked first —
 * refused with the reason and the date, or confirmed in a dialog.
 */
const BecomeFanButton = ({
  clubId,
  name,
  isAthlete = false
}: BecomeFanButtonProps) => {
  const t = useTranslations('club.becomeFan')
  const queryClient = useQueryClient()
  const { data: me } = useUserMe()
  const { mutate, isPending } = useBecomeFan()
  const [step, setStep] = useState<Step>({ kind: 'idle' })
  const [isChecking, setIsChecking] = useState(false)
  // A double click must not send the choice (and its analytics) twice.
  const busyRef = useRef(false)

  const favorite = me?.favorite_club
  const currentId = me?.user?.favorite_club_id || undefined
  const currentName =
    (currentId &&
      ((favorite as { display_name?: string } | undefined)?.display_name ||
        favorite?.favorite_club_name)) ||
    me?.waitlist_club?.club_name ||
    ''
  const hasFavorite = Boolean(currentId || me?.waitlist_club?.id)

  const choose = (switching: boolean) => {
    if (busyRef.current) {
      return
    }
    busyRef.current = true
    mutate(
      { clubId, isAthlete, previousClubId: currentId },
      {
        onSettled: () => {
          busyRef.current = false
        },
        onSuccess: () => {
          setStep({ kind: 'idle' })
          showToast({
            type: 'success',
            title: switching ? t('switchedToast') : t('becameFanToast')
          })
        },
        onError: async error => {
          const block = await blockFromError(error)
          if (block) {
            setStep({ kind: 'blocked', block })
            return
          }
          showToast({ type: 'error', title: t('error.network') })
        }
      }
    )
  }

  const handleClick = async () => {
    // Until /me is in, «no favourite» is unknown: a click then could
    // overwrite the current one without the switch dialog.
    if (busyRef.current || isChecking || !me?.user) {
      return
    }
    if (!hasFavorite) {
      choose(false)
      return
    }
    busyRef.current = true
    setIsChecking(true)
    try {
      const availability = await queryClient.fetchQuery({
        queryKey: [FAVORITE_CHANGE_KEY, me?.user?.id],
        queryFn: fetchFavoriteChange,
        staleTime: 0
      })
      const block = blockFromAvailability(availability)
      setStep(
        block
          ? { kind: 'blocked', block }
          : { kind: 'switch', currentName: currentName || t('currentFallback') }
      )
    } catch {
      // The check is advisory: the PUT enforces the rules, so a failed read
      // still lets the fan confirm (a refusal then shows the reason).
      setStep({
        kind: 'switch',
        currentName: currentName || t('currentFallback')
      })
    } finally {
      busyRef.current = false
      setIsChecking(false)
    }
  }

  return (
    <>
      <Button
        className={S.trigger}
        color="secondary"
        type="button"
        onClick={handleClick}
        disabled={isPending || isChecking || !me?.user}
      >
        {t('button')}
        <HeartIcon className={S.heartIcon} />
      </Button>

      <FavoriteSwitchDialog
        open={step.kind === 'switch'}
        currentName={step.kind === 'switch' ? step.currentName : ''}
        newName={name}
        pending={isPending}
        onConfirm={() => choose(true)}
        onCancel={() => setStep({ kind: 'idle' })}
      />
      <FavoriteBlockedDialog
        open={step.kind === 'blocked'}
        block={step.kind === 'blocked' ? step.block : null}
        onClose={() => setStep({ kind: 'idle' })}
      />
    </>
  )
}

export default BecomeFanButton
